import { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// scroll-mt must clear the sticky PersonaToggle, or a JumpNav tap tucks the
// section heading underneath it. On mobile that element is ~82px (8px pad + a
// 44px pill row + the italic tagline line + 8px pad), so scroll-mt-16 (64px)
// was short and the heading landed behind the toggle. 88px clears it. At sm+
// the toggle is not sticky over the content in the same way, so 4 stands.

export default function Section({ id, title, eyebrow, collapsible = false, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  if (!collapsible) {
    return (
      <section id={id} className="scroll-mt-[88px] border-t border-navy-100 py-6 sm:scroll-mt-4">
        <Eyebrow text={eyebrow} />
        <h2 className="mb-3 font-display text-xl font-semibold text-navy-900 sm:text-2xl">
          {title}
        </h2>
        <div className="text-navy-800">{children}</div>
      </section>
    );
  }

  return (
    <section id={id} className="scroll-mt-[88px] border-t border-navy-100 py-6 sm:scroll-mt-4">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between text-left"
        aria-expanded={open}
      >
        <div className="min-w-0 flex-1">
          <Eyebrow text={eyebrow} />
          <h2 className="font-display text-xl font-semibold text-navy-900 sm:text-2xl">
            {title}
          </h2>
        </div>
        <ChevronDown
          className={['h-5 w-5 shrink-0 text-navy-500 transition-transform', open ? 'rotate-180' : ''].join(' ')}
        />
      </button>
      {open && <div className="mt-3 text-navy-800">{children}</div>}
    </section>
  );
}

// Almanac kicker: gold small caps with a hairline rule running out to the right.
// `id` lets a section name itself by its kicker through aria-labelledby.
// `note` sets a short aside at the end of the rule at sm+, the way the
// persona row sets its tagline. On phones it takes its own line under the
// kicker: beside it, "Sunday's lands by 6:30 AM ET" crushed the rule to a stub
// and broke "6:30 / AM ET" at 390px.
export function Eyebrow({ text, id, note }) {
  if (!text) return null;
  return (
    <div className="mb-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
      <p id={id} className="shrink-0 text-xs font-medium uppercase tracking-[0.18em] text-cream-600">
        {text}
      </p>
      <div className="h-px min-w-4 flex-1 bg-navy-100" />
      {note && (
        <p className="min-w-0 basis-full font-serif text-sm text-navy-600 sm:basis-auto sm:text-right">
          {note}
        </p>
      )}
    </div>
  );
}
